'use client';

import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import * as React from 'react';

import type { RelatedBundle } from '@/lib/api/event/models';
import { ChartContainer } from '@/lib/components/Chart/ChartContainer';
import type { SeriesData } from '@/lib/components/Chart/types';
import { ShareButton } from '@/lib/components/Event/ShareButton';
import { EventImage } from '@/lib/components/EventImage';
import { Link } from '@/lib/routing';
import { AnimatedIcon } from '@/lib/ui-kit/animated-icon';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { EventBannerRelated } from '@/lib/ui-kit-v2/event-banner-related';
import { OutpollWatermark } from '@/lib/ui-kit-v2/outpoll-watermark';

interface EventBannerSeries {
  id: string;
  label: string;
  percent: number;
  color: string;
  data: { time: number; value: number }[];
}

interface EventBannerStat {
  label: string;
  value: string;
}

interface EventBannerMeta {
  endDate?: string;
  traders?: string;
  volume?: string;
  news?: string;
}

interface EventBannerEvent {
  id?: string;
  slug?: string;
  href?: string;
  image: string;
  title: string;
  meta?: EventBannerMeta;
  stats: EventBannerStat[];
  /** Fallback copy — shown only when `related` has no content at all. */
  description?: string;
  /** Related events / comments / news, rotated in place of the description. */
  related?: RelatedBundle;
  isAmbassador?: boolean;
  series: EventBannerSeries[];
  /** Time window in seconds. Default: 7 days. Ignored when `chartsData` is set
   *  (ChartContainer derives the window from selectedTimeRange + eventStartSec). */
  timeWindow?: number;
  isFavorite?: boolean;
  /** Raw chart history rows in `SeriesData` shape — fed straight to
   *  `ChartContainer` so banner uses the exact same render path as the event
   *  page (padding, window clamping, formatters, axis spacing). */
  chartsData?: SeriesData[];
  /** Event creation time in seconds — lets ChartContainer treat ALL window
   *  as `now - eventStart` even when the order-form-store is not populated. */
  eventStartSec?: number;
}

interface EventBannerProps {
  events: EventBannerEvent[];
  defaultIndex?: number;
  index?: number;
  onIndexChange?: (index: number) => void;
  onBookmark?: (event: EventBannerEvent) => void;
  onEventClick?: (event: EventBannerEvent) => void;
  className?: string;
  autoplayMs?: number;
}

// Slide transition. A single "settle" curve (fast out, long soft landing) for
// both the incoming and the outgoing slide so they read as one movement.
const SLIDE_EASE: [number, number, number, number] = [0.32, 0.72, 0, 1];
const SLIDE_DISTANCE = 56;
const SLIDE_IN_DURATION = 0.42;
const SLIDE_OUT_DURATION = 0.32;

/** `custom` payload — variants need the direction at the moment a slide leaves,
 *  which is only reachable through AnimatePresence's `custom`. */
interface SlideCustom {
  direction: 1 | -1;
  offset: number;
}

const slideVariants = {
  enter: ({ direction, offset }: SlideCustom) => ({ opacity: 0, x: direction * offset }),
  center: { opacity: 1, x: 0 },
  exit: ({ direction, offset }: SlideCustom) => ({ opacity: 0, x: direction * -offset }),
};

function EventBanner({
  events,
  defaultIndex = 0,
  index: controlledIndex,
  onIndexChange,
  onBookmark,
  onEventClick,
  className,
  autoplayMs = 5000,
}: EventBannerProps) {
  const [internalIndex, setInternalIndex] = React.useState(
    Math.min(defaultIndex, Math.max(0, events.length - 1)),
  );
  const [direction, setDirection] = React.useState<1 | -1>(1);
  // Measured height of the live slide. Slides differ in height (title lines,
  // outcome count), so the viewport eases between them rather than snapping
  // the page around — and never collapses to 0 mid-transition.
  const [viewportHeight, setViewportHeight] = React.useState<number | null>(null);
  // Autoplay runs only while the banner is on screen and the tab is visible.
  // Hover/focus pausing is done in CSS so it costs no re-render.
  const [autoplayPaused, setAutoplayPaused] = React.useState(false);

  const reduceMotion = useReducedMotion();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const heightObserver = React.useRef<ResizeObserver | null>(null);

  const total = events.length;
  const activeIndex = total === 0 ? 0 : Math.min(controlledIndex ?? internalIndex, total - 1);
  const event: EventBannerEvent | undefined = events[activeIndex];

  const changeIndex = React.useCallback(
    (next: number) => {
      if (total === 0) return;
      const clamped = ((next % total) + total) % total;
      if (clamped === activeIndex) return;
      // Compare the raw target, not the wrapped one, so last → first still
      // travels forwards.
      setDirection(next > activeIndex ? 1 : -1);
      if (controlledIndex === undefined) setInternalIndex(clamped);
      onIndexChange?.(clamped);
    },
    [activeIndex, controlledIndex, onIndexChange, total],
  );

  // Attached to whichever slide is live. Detaches (`null`) are ignored on
  // purpose: the leaving slide drops the ref in the same commit the new one
  // takes it, and only the latest attach should own the observer.
  const measureRef = React.useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof ResizeObserver === 'undefined') return;
    heightObserver.current?.disconnect();
    const sync = () => setViewportHeight(node.getBoundingClientRect().height);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    heightObserver.current = observer;
  }, []);

  React.useEffect(() => () => heightObserver.current?.disconnect(), []);

  // Off-screen or background-tab autoplay only burns CPU on slides nobody is
  // looking at.
  React.useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let visible = true;
    const sync = () => setAutoplayPaused(!visible || document.hidden);
    const observer = new IntersectionObserver((entries) => {
      visible = entries[entries.length - 1].isIntersecting;
      sync();
    });
    observer.observe(el);
    document.addEventListener('visibilitychange', sync);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const handleProgressEnd = React.useCallback(() => {
    changeIndex(activeIndex + 1);
  }, [activeIndex, changeIndex]);

  const slideCustom = React.useMemo<SlideCustom>(
    () => ({ direction, offset: reduceMotion ? 0 : SLIDE_DISTANCE }),
    [direction, reduceMotion],
  );

  if (!event) return null;

  const autoplayEnabled = total > 1 && autoplayMs > 0;

  return (
    <div
      ref={rootRef}
      className={cn(
        'group/banner flex flex-col gap-6 overflow-hidden rounded-[16px] bg-v2-surface-secondary px-4 py-6',
        'min-[900px]:gap-8 min-[900px]:px-5',
        'shadow-[0px_0px_20px_0px_rgba(0,41,98,0.1)]',
        className,
      )}
    >
      {/* Slider viewport — height follows the live slide and is eased, so the
          box neither collapses between slides nor snaps the page. */}
      <div
        className="relative w-full overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none"
        style={{ height: viewportHeight ?? undefined }}
      >
        {/* `initial={false}` keeps the first paint settled — the banner is
            above the fold. No `mode="wait"`: the slides cross over instead of
            the box going blank for a full exit+enter cycle. */}
        <AnimatePresence initial={false} custom={slideCustom}>
          <EventBannerPresenceSlide
            key={event.id ?? activeIndex}
            event={event}
            custom={slideCustom}
            reduceMotion={Boolean(reduceMotion)}
            measureRef={measureRef}
            onBookmark={onBookmark ? () => onBookmark(event) : undefined}
            onEventClick={onEventClick ? () => onEventClick(event) : undefined}
          />
        </AnimatePresence>
      </div>

      {/* Pagination */}
      {total > 1 && (
        <div className="flex items-center justify-between">
          <IconButton aria-label="Previous" onClick={() => changeIndex(activeIndex - 1)}>
            <ChevronIcon direction="left" />
          </IconButton>
          <div className="flex items-center gap-1.5">
            {events.map((e, i) => {
              const active = i === activeIndex;
              return (
                <button
                  type="button"
                  key={e.id ?? i}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={active || undefined}
                  onClick={() => changeIndex(i)}
                  className={cn(
                    'relative h-1.5 cursor-pointer overflow-hidden rounded-full bg-v2-text-secondary/40 outline-none transition-[width,opacity] duration-300 ease-out',
                    active ? 'w-8' : 'w-1.5 opacity-60 hover:opacity-90',
                  )}
                >
                  {/* The autoplay clock itself: a CSS animation, so the
                      countdown neither re-renders the banner every frame nor
                      drifts, and `animationend` is what advances the slide. */}
                  {active && autoplayEnabled && (
                    <span
                      aria-hidden
                      onAnimationEnd={handleProgressEnd}
                      style={{
                        animationDuration: `${autoplayMs}ms`,
                        // Left unset when running so the CSS hover/focus pause
                        // below isn't overridden by an inline value.
                        animationPlayState: autoplayPaused ? 'paused' : undefined,
                      }}
                      className={cn(
                        'absolute inset-0 origin-left rounded-full bg-v2-text-primary [transform:scaleX(0)]',
                        '[animation-name:event-banner-progress] [animation-timing-function:linear] [animation-fill-mode:forwards]',
                        'group-hover/banner:[animation-play-state:paused] group-focus-within/banner:[animation-play-state:paused]',
                        // Reduced motion: no autoplay (nothing ends the
                        // animation), pill shown filled instead of empty.
                        'motion-reduce:[animation-name:none] motion-reduce:[transform:scaleX(1)]',
                      )}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <IconButton aria-label="Next" onClick={() => changeIndex(activeIndex + 1)}>
            <ChevronIcon direction="right" />
          </IconButton>
        </div>
      )}
    </div>
  );
}

interface EventBannerPresenceSlideProps {
  event: EventBannerEvent;
  custom: SlideCustom;
  reduceMotion: boolean;
  measureRef: (node: HTMLDivElement | null) => void;
  onBookmark?: () => void;
  onEventClick?: () => void;
}

/**
 * One slide inside the AnimatePresence list. Stays mounted while it animates
 * out — same DOM node, so its chart and image are never re-created (a cloned
 * copy would repaint from an empty canvas, which reads as a flicker). It only
 * moves out of the layout flow, letting the incoming slide own the height.
 */
function EventBannerPresenceSlide({
  event,
  custom,
  reduceMotion,
  measureRef,
  onBookmark,
  onEventClick,
}: EventBannerPresenceSlideProps) {
  const isPresent = useIsPresent();

  return (
    <motion.div
      ref={isPresent ? measureRef : null}
      custom={custom}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{
        duration: reduceMotion ? 0.2 : isPresent ? SLIDE_IN_DURATION : SLIDE_OUT_DURATION,
        ease: SLIDE_EASE,
        // Opacity resolves ahead of the travel: the two slides overlap for a
        // moment, and a slow fade would show both texts at once.
        opacity: {
          duration: reduceMotion ? 0.18 : isPresent ? 0.24 : 0.18,
          ease: isPresent ? 'easeOut' : 'easeIn',
        },
      }}
      // Out of flow only once it starts leaving, so the two slides overlap
      // instead of stacking and doubling the banner's height.
      className={cn('w-full', !isPresent && 'pointer-events-none absolute inset-x-0 top-0')}
      aria-hidden={!isPresent || undefined}
      inert={!isPresent || undefined}
    >
      <EventBannerSlide event={event} onBookmark={onBookmark} onEventClick={onEventClick} />
    </motion.div>
  );
}

interface EventBannerSlideProps {
  event: EventBannerEvent;
  onBookmark?: () => void;
  onEventClick?: () => void;
}

function EventBannerSlide({ event, onBookmark, onEventClick }: EventBannerSlideProps) {
  const shareUrl = event.href ?? (event.slug ? `/event/${event.slug}` : undefined);
  const { image, title, meta, stats, description, series, chartsData, eventStartSec } = event;
  // Derive ChartContainer items from chartsData (preferred) or fall back to
  // the legacy series shape so this still works for callers that haven't
  // migrated yet.
  const chartItems: SeriesData[] = React.useMemo(() => {
    if (chartsData && chartsData.length > 0) return chartsData;
    return series.map((s, i) => ({
      id: s.id,
      name: s.label,
      history: s.data.map((d) => ({ t: d.time, p: d.value / 100 })),
      originalIndex: i,
    }));
  }, [chartsData, series]);

  const metaItems = [meta?.endDate, meta?.traders, meta?.volume, meta?.news].filter(
    (item): item is string => Boolean(item),
  );

  // Bookmark + share. Rendered in the header on desktop and in the footer row
  // on mobile, so it's a small helper to avoid duplicating the markup.
  const renderActions = (displayClassName: string) => (
    <div
      className={cn('shrink-0 items-center gap-2', displayClassName)}
      data-banner-action
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={event.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={event.isFavorite || undefined}
        onClick={(e) => {
          e.stopPropagation();
          onBookmark?.();
        }}
        className="flex size-10 shrink-0 cursor-pointer items-center justify-center text-v2-icon-primary
          outline-none transition-colors hover:text-v2-icon-primary-hover"
      >
        {event.isFavorite ? (
          <Icon name="BookmarkFilled" size={20} />
        ) : (
          <AnimatedIcon name="bookmark" size={20} className="lottie-current" />
        )}
      </button>
      <ShareButton
        size={20}
        title={event.title}
        shareUrl={shareUrl}
        className="flex size-10 shrink-0 items-center justify-center outline-none"
      />
    </div>
  );

  const handleSlideClick = onEventClick
    ? (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('[data-banner-action]')) return;
        onEventClick();
      }
    : undefined;

  const handleSlideKeyDown = onEventClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEventClick();
        }
      }
    : undefined;

  return (
    <div
      className={cn('flex flex-col gap-8', onEventClick && 'cursor-pointer')}
      onClick={handleSlideClick}
      onKeyDown={handleSlideKeyDown}
      role={onEventClick ? 'link' : undefined}
      tabIndex={onEventClick ? 0 : undefined}
    >
      {/* Header */}
      <div className="flex items-center gap-3 min-[900px]:items-start min-[900px]:gap-4">
        <div className="relative size-11 shrink-0 overflow-hidden rounded-[8px] border border-v2-border min-[900px]:size-[60px]">
          <EventImage src={image} alt={title} fill sizes="60px" className="object-cover" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 min-[900px]:min-h-[60px] min-[900px]:gap-2">
          <h2 className="line-clamp-2 text-[16px] font-medium leading-[22px] text-v2-text-primary min-[900px]:line-clamp-1 min-[900px]:text-[24px] min-[900px]:leading-8">
            {title}
          </h2>
          {/* Meta (volume/traders/…) sits under the title on desktop; on mobile
              the volume moves to the footer row instead. */}
          {metaItems.length > 0 && (
            <div className="hidden flex-wrap items-center gap-2 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary min-[900px]:flex">
              {metaItems.map((item, index) => (
                <React.Fragment key={item}>
                  {index > 0 && (
                    <span
                      aria-hidden
                      className="size-[3px] shrink-0 rounded-full bg-v2-text-secondary"
                    />
                  )}
                  <span className="whitespace-nowrap">{item}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {renderActions('hidden min-[900px]:flex')}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-6 min-[900px]:flex-row min-[900px]:gap-8">
        {/* Desktop height matches the chart column (24 legend + 16 gap + 299
            chart) so the slide can't grow with the related feed's content. */}
        <div className="flex w-full shrink-0 flex-col gap-3 min-[900px]:h-[339px] min-[900px]:w-[400px] min-[900px]:gap-4">
          {series.length === 1 ? (
            // Hugs its content — the related feed below takes the leftover
            // space instead of leaving a fixed-height gap.
            <div className="flex shrink-0 flex-col text-v2-text-primary">
              <BinaryOutcome
                series={series[0]}
                href={event.href ?? (event.slug ? `/event/${event.slug}` : undefined)}
                variant={event.isAmbassador ? 'premium' : 'default'}
              />
            </div>
          ) : (
            <div className="scrollbar-hide flex shrink-0 flex-col overflow-y-auto text-v2-text-primary min-[900px]:max-h-[204px]">
              {stats.map((stat, i) => (
                <div
                  key={`${stat.label}-${i}`}
                  className="flex shrink-0 items-center gap-3 py-1 min-[900px]:py-2"
                >
                  <span className="min-w-0 flex-1 text-[13px] font-normal leading-5 min-[900px]:text-[15px] min-[900px]:font-semibold">
                    {stat.label}
                  </span>
                  <span className="shrink-0 text-[16px] font-medium leading-[22px] min-[900px]:text-[24px] min-[900px]:font-semibold min-[900px]:leading-8">
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Always rendered — reserves space even when there's nothing to
              show, otherwise the slide height jumps between slides. */}
          <EventBannerRelated related={event.related} description={description} />
        </div>

        {/* Chart — desktop only. The mobile banner design omits it. */}
        <div className="hidden min-w-0 flex-1 flex-col gap-4 min-[900px]:flex">
          {/* Fixed height + clipped: a wrapping legend used to grow the chart
              column, which made the slide (and the related feed) jump. */}
          <div className="flex h-6 shrink-0 items-center justify-between gap-3 overflow-hidden">
            <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
              {series.map((s) => (
                <div key={s.id} className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="text-[12px] font-medium leading-[14px] text-v2-text-secondary whitespace-nowrap">
                    {s.label} {formatPercent(s.percent)}
                  </span>
                </div>
              ))}
            </div>
            <OutpollWatermark className="shrink-0 text-v2-text-primary opacity-10" />
          </div>

          <div className="relative h-[299px] w-full">
            <ChartContainer
              chartId={`banner-${event.id ?? event.slug ?? title}`}
              items={chartItems}
              eventStartSecOverride={eventStartSec}
              showHeader={false}
              showTimeControls={false}
              height={299}
              animation={{
                lineRevealDuration: 1800,
                sparkDuration: 900,
                morphDuration: 800,
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer — mobile only: volume on the left, actions on the right.
          Negative margin cancels the slide's gap-8 so it sits right under the
          related feed. */}
      <div className="-mt-8 flex items-center justify-between min-[900px]:hidden">
        {meta?.volume ? (
          <span className="text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary">
            {meta.volume}
          </span>
        ) : (
          <span aria-hidden />
        )}
        {renderActions('flex')}
      </div>
    </div>
  );
}

function BinaryOutcome({
  series,
  href,
  variant = 'default',
}: {
  series: EventBannerSeries;
  href?: string;
  variant?: 'default' | 'premium';
}) {
  const t = useTranslations('common');
  const yesPercent = series.percent;
  const noPercent = Math.max(0, 100 - yesPercent);
  const trend = React.useMemo(() => {
    if (!series.data || series.data.length < 2) return 0;
    const first = series.data[0].value;
    const last = series.data[series.data.length - 1].value;
    return last - first;
  }, [series.data]);

  const baseBtnCls =
    'flex min-w-0 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-[10px] px-5 py-3 text-center text-[16px] leading-[22px] outline-none transition-colors';

  const yesCls =
    variant === 'premium'
      ? cn(baseBtnCls, 'font-bold text-white')
      : cn(
          baseBtnCls,
          'font-medium text-v2-green bg-v2-green-surface hover:bg-[#129B44] hover:text-white',
        );

  const noCls =
    variant === 'premium'
      ? cn(baseBtnCls, 'font-bold text-white')
      : cn(
          baseBtnCls,
          'font-medium text-red-400 bg-v2-red-surface hover:bg-[#E13736] hover:text-white',
        );

  const yesStyle =
    variant === 'premium'
      ? { backgroundImage: 'linear-gradient(221.68deg, #17d271 36.27%, #00813e 101.71%)' }
      : undefined;
  const noStyle =
    variant === 'premium'
      ? { backgroundImage: 'linear-gradient(221.68deg, #fb4e4b 36.27%, #9b0300 101.71%)' }
      : undefined;

  const stopBubble = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1.5 text-[15px] font-semibold leading-5 text-v2-text-primary">
        <span>{formatPercent(yesPercent)} Chance</span>
        {trend !== 0 && (
          <Icon
            name={trend > 0 ? 'ArrowUp' : 'ArrowDown'}
            size={20}
            className={
              trend > 0 ? 'text-v2-btn-premium-green-light' : 'text-v2-btn-premium-red-light'
            }
          />
        )}
      </div>
      <div className="flex w-full items-center gap-2">
        {href ? (
          <Link
            href={href}
            data-banner-action
            onClick={stopBubble}
            className={yesCls}
            style={yesStyle}
          >
            {t('yes')} {formatPercent(yesPercent)}
          </Link>
        ) : (
          <button
            type="button"
            data-banner-action
            onClick={stopBubble}
            className={yesCls}
            style={yesStyle}
          >
            {t('yes')} {formatPercent(yesPercent)}
          </button>
        )}
        {href ? (
          <Link
            href={href}
            data-banner-action
            onClick={stopBubble}
            className={noCls}
            style={noStyle}
          >
            {t('no')} {formatPercent(noPercent)}
          </Link>
        ) : (
          <button
            type="button"
            data-banner-action
            onClick={stopBubble}
            className={noCls}
            style={noStyle}
          >
            {t('no')} {formatPercent(noPercent)}
          </button>
        )}
      </div>
    </div>
  );
}

function IconButton({
  children,
  className,
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none transition-colors',
        'hover:bg-v2-btn-secondary disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="9"
      height="15"
      viewBox="0 0 6 10"
      fill="none"
      aria-hidden
      className={cn('text-v2-text-secondary', direction === 'left' && '-scale-x-100')}
    >
      <path
        d="M5.33191 4.27631C5.57222 4.57089 5.55476 5.00561 5.28016 5.28022L1.28016 9.28022C0.987264 9.57311 0.512503 9.57311 0.21961 9.28022C-0.0732832 8.98732 -0.0732832 8.51256 0.21961 8.21967L3.68934 4.74994L0.21961 1.28022C-0.0732837 0.987323 -0.0732837 0.512563 0.21961 0.21967C0.512503 -0.0732233 0.987264 -0.0732233 1.28016 0.21967L5.28016 4.21967L5.33191 4.27631Z"
        fill="currentColor"
      />
    </svg>
  );
}

function formatPercent(value: number): string {
  if (value < 1) return '<1%';
  return `${Math.round(value)}%`;
}

export type {
  EventBannerEvent,
  EventBannerMeta,
  EventBannerProps,
  EventBannerSeries,
  EventBannerStat,
};
export { EventBanner };
